import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import { getComicById, getRelatedComics, comics } from '@/mocks/comics'
import type { Comic } from '@/mocks/comics'

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info')
  const comicId = Number(id)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [id])

  const comic = getComicById(comicId)

  if (!comic) {
    const fallback = comics[0]
    return <ArticleContent comic={fallback} activeTab={activeTab} setActiveTab={setActiveTab} />
  }

  return <ArticleContent comic={comic} activeTab={activeTab} setActiveTab={setActiveTab} />
}

function ArticleContent({ comic, activeTab, setActiveTab }: { comic: Comic; activeTab: 'info' | 'chapters'; setActiveTab: (t: 'info' | 'chapters') => void }) {
  const related = getRelatedComics(comic, 4)
  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第 ${comic.chapters - i} 话${i === 0 ? ' - 最新' : ''}`,
    date: `${2025 - Math.floor(i / 12)}-${String(12 - (i % 12)).padStart(2, '0')}-${String(Math.floor(Math.random() * 28) + 1).padStart(2, '0')}`,
  }))

  return (
    <>
      <Navbar />

      <main>
        <section className="pt-24 pb-8 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">{comic.category}</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="w-full lg:w-[300px] shrink-0">
                <div className="sticky top-24">
                  <div className="rounded-2xl overflow-hidden mb-4" style={{ width: '100%', maxWidth: '300px' }}>
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full object-cover object-top"
                      style={{ aspectRatio: '3/4' }}
                    />
                  </div>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="block w-full py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer text-center"
                  >
                    开始阅读
                  </Link>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-foreground-100 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="flex items-center gap-0.5 text-sm text-foreground-700">
                    <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-xs"></i>
                    </span>
                    <strong>{comic.rating}</strong>
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                  <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="inline-block px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-6 w-fit">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeTab === 'info' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    作品详情
                  </button>
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeTab === 'chapters' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    章节列表 ({comic.chapters}话)
                  </button>
                </div>

                {activeTab === 'info' && (
                  <div>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                      {comic.description}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 bg-background-100 rounded-2xl">
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">总章节</p>
                        <p className="font-heading text-lg font-bold text-foreground-900">{comic.chapters}话</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">总阅读量</p>
                        <p className="font-heading text-lg font-bold text-foreground-900">{comic.views}</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">评分</p>
                        <p className="font-heading text-lg font-bold text-foreground-900">{comic.rating} / 5.0</p>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500 mb-1">更新状态</p>
                        <p className="font-heading text-lg font-bold text-primary-500">{comic.status}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'chapters' && (
                  <div className="space-y-2">
                    {chapterList.map((ch) => (
                      <div
                        key={ch.num}
                        className="flex items-center justify-between p-4 bg-background-100 rounded-xl hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold shrink-0">
                            {ch.num}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-foreground-900">{ch.title}</p>
                            <p className="text-xs text-foreground-500">{ch.date}</p>
                          </div>
                        </div>
                        <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                          <i className="ri-arrow-right-s-line"></i>
                        </span>
                      </div>
                    ))}
                    {comic.chapters > 20 && (
                      <div className="text-center pt-4">
                        <button className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap">
                          加载更多章节
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-arrow-down-line text-sm"></i>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100" aria-labelledby="related-title">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <h2 id="related-title" className="font-heading text-2xl font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/article/${r.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/50 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full overflow-hidden" style={{ height: '200px' }}>
                      <img
                        src={r.cover}
                        alt={r.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-1">{r.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{r.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: comic.title,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: '2025-01-15',
        dateModified: '2025-07-01',
        image: comic.cover,
        publisher: {
          '@type': 'Organization',
          name: '韩漫漫画网站',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `http://tenford.cn/article/${comic.id}`,
        },
      })}} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://tenford.cn/' },
          { '@type': 'ListItem', position: 2, name: comic.category, item: `http://tenford.cn/category/${comic.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: comic.title, item: `http://tenford.cn/article/${comic.id}` },
        ],
      })}} />
    </>
  )
}